import discountApi from "@/services/api/discount";

let nextTempRuleId = 0;

const emptyRule = () => ({
  id: null,
  _tempId: nextTempRuleId++,
  name: "",
  amount: "",
  amount_type: { value: "fixed", key: "Fixed" },
  applies_to: { value: "everyone", key: "Everyone" },
  include_extras: false
});

const emptyDiscount = () => ({
  id: null,
  code: "",
  description: "",
  usage_limit: null,
  active: false,
  expiry_date: null,
  automatic: false,
  discount_type: { value: "discount", key: "Discount" },
  applies_to: { value: "all", key: "All" },
  restricted_to_adventures: [],
  rules: []
});

const unwrap = payload =>
  payload && typeof payload === "object" && "data" in payload
    ? payload.data
    : payload;

const normalizeRule = r => ({
  ...r,
  _tempId: r?._tempId ?? nextTempRuleId++,
  amount_type:
    r?.amount_type && typeof r.amount_type === "object"
      ? r.amount_type
      : { value: r?.amount_type || "", key: "" },
  applies_to:
    r?.applies_to && typeof r.applies_to === "object"
      ? r.applies_to
      : { value: r?.applies_to || "", key: "" }
});

const normalizeDiscount = d => {
  if (!d) return null;

  return {
    ...d,
    discount_type:
      d?.discount_type && typeof d.discount_type === "object"
        ? d.discount_type
        : { value: d?.discount_type || "", key: "" },
    applies_to: (() => {
      const appliesTo =
        d?.applies_to && typeof d.applies_to === "object"
          ? d.applies_to
          : { value: d?.applies_to || "", key: "" };
      if (appliesTo.value === "restricted_to_adventures") {
        return { ...appliesTo, value: "selected_adventures" };
      }
      if (appliesTo.value === "selected_adventures" && !appliesTo.key) {
        return { ...appliesTo, key: "Selected Adventures" };
      }
      return appliesTo;
    })(),
    automatic: !!d?.automatic,
    restricted_to_adventures: Array.isArray(d?.restricted_to_adventures)
      ? d.restricted_to_adventures
      : [],
    rules: Array.isArray(d.rules) ? d.rules.map(normalizeRule) : []
  };
};

const mapDiscountToApi = discount => {
  const { restricted_to_adventures, ...rest } = discount || {};
  const payload = rest || {};

  return {
    ...payload,
    discount_type: discount?.discount_type?.value ?? discount?.discount_type,
    applies_to: discount?.applies_to?.value ?? discount?.applies_to,
    restricted_to_adventures: Array.isArray(restricted_to_adventures)
      ? restricted_to_adventures
      : []
  };
};

const mapRuleToApi = rule => ({
  name: rule.name,
  amount: rule.amount,
  include_extras: rule.include_extras,
  amount_type: rule.amount_type?.value ?? rule.amount_type,
  applies_to: rule.applies_to?.value ?? rule.applies_to
});

export default {
  namespaced: true,

  state: () => ({
    list: [],
    selected: null,
    loading: false,
    bookings: []
  }),

  getters: {
    discounts: s => s.list,
    selectedDiscount: s => s.selected,
    isNewDiscount: s => !s.selected?.id,
    discountBookings: s => s.bookings
  },

  mutations: {
    SET_DISCOUNTS(state, discounts) {
      state.list = discounts;
    },
    SET_SELECTED(state, discount) {
      state.selected = discount;
    },
    SET_BOOKINGS(state, bookings) {
      state.bookings = bookings;
    },

    UPDATE_DISCOUNT_FIELD(state, { field, value }) {
      if (!state.selected) return;
      state.selected[field] = value;
    },

    ADD_RULE(state) {
      if (!state.selected) return;
      state.selected.rules.push(emptyRule());
    },

    UPDATE_RULE_FIELD(state, { ruleIndex, field, value }) {
      const rule = state.selected?.rules?.[ruleIndex];
      if (!rule) return;

      if (field === "amount_type" || field === "applies_to") {
        const current =
          rule[field] && typeof rule[field] === "object"
            ? rule[field]
            : { value: "", key: "" };

        rule[field] =
          typeof value === "object"
            ? { ...current, ...value }
            : { ...current, value };

        return;
      }

      rule[field] = value;
    },

    REMOVE_RULE(state, ruleIndex) {
      if (!state.selected?.rules) return;
      state.selected.rules.splice(ruleIndex, 1);
    }
  },

  actions: {
    async fetchDiscounts({ commit }) {
      const payload = await discountApi.getDiscountCodes();
      const list = unwrap(payload);
      commit("SET_DISCOUNTS", list);
    },

    async fetchDiscount({ commit }, id) {
      const payload = await discountApi.getDiscountCode(id);
      const discount = normalizeDiscount(unwrap(payload));
      commit("SET_SELECTED", discount);
    },

    async fetchDiscountBookings({ commit }, discountId) {
      if (!discountId) {
        commit("SET_BOOKINGS", []);
        return;
      }

      const payload = await discountApi.getDiscountBookings(discountId);
      const data = unwrap(payload);
      const bookings = data?.bookings || [];
      commit("SET_BOOKINGS", bookings);
    },

    clearSelected({ commit }) {
      commit("SET_SELECTED", null);
    },

    selectDiscount({ commit }, discount) {
      const normalized = normalizeDiscount(structuredClone(discount));
      commit("SET_SELECTED", normalized);
    },

    newDiscount({ commit }) {
      commit("SET_SELECTED", emptyDiscount());
    },

    updateDiscountField({ commit }, payload) {
      commit("UPDATE_DISCOUNT_FIELD", payload);
    },

    updateRuleField({ commit }, payload) {
      commit("UPDATE_RULE_FIELD", payload);
    },

    addRule({ commit, state }) {
      if (!state.selected) return;
      commit("ADD_RULE");
    },

    async saveDiscount({ state, commit, dispatch }) {
      const payload = mapDiscountToApi(state.selected);
      const selectedId = state.selected?.id ?? payload.id;

      const result = payload.id
        ? await discountApi.updateDiscountCode(payload)
        : await discountApi.createDiscountCode(payload);

      const responseData = unwrap(result);
      const resolvedDiscount = Array.isArray(responseData)
        ? responseData.find(item => item?.id == selectedId)
        : responseData;
      const savedDiscount = normalizeDiscount(
        resolvedDiscount ?? structuredClone(state.selected)
      );
      commit("SET_SELECTED", savedDiscount);

      await dispatch("fetchDiscounts");
      commit("SET_SELECTED", savedDiscount);
    },

    async saveRule({ state, commit, dispatch }, ruleIndex) {
      const discount = state.selected;
      if (!discount?.id) throw new Error("Save the discount first.");

      const rule = discount.rules[ruleIndex];
      const rulesetPayload = mapRuleToApi(rule);

      if (!rulesetPayload.amount_type || !rulesetPayload.applies_to) {
        throw new Error("Select amount_type and applies_to before saving");
      }

      const result = rule.id
        ? await discountApi.updateRuleSet({
            discountCode: discount,
            ruleset: { id: rule.id, ...rulesetPayload }
          })
        : await discountApi.addRuleSet({
            discountCode: discount,
            ruleset: rulesetPayload
          });

      const updatedDiscount = normalizeDiscount(unwrap(result));
      commit("SET_SELECTED", updatedDiscount);

      await dispatch("fetchDiscounts");
    },

    async deleteDiscount({ state, commit, dispatch }, discount) {
      await discountApi.deleteDiscountCode(discount);

      await dispatch("fetchDiscounts");

      if (state.selected?.id === discount.id) {
        commit("SET_SELECTED", null);
      }
    },

    async deleteRule({ state, commit, dispatch }, ruleIndex) {
      const discount = state.selected;
      if (!discount) return;

      const rule = discount.rules[ruleIndex];

      commit("REMOVE_RULE", ruleIndex);

      if (!discount.id || !rule?.id) return;

      await discountApi.removeRuleSet({
        discountCode: discount,
        ruleset: rule
      });

      await dispatch("fetchDiscount", discount.id);
      await dispatch("fetchDiscounts");
    }
  }
};
